/**
 * AP-773 — the worksheet side of a history step.
 *
 * Steps never hold React state setters: the worksheet registers an applier
 * while mounted, and steps read/write through it. A snapshot is the part of
 * the report editor state a §3.1 action can change on the worksheet —
 * measurement rows (values, units, sources, derived values, annotations), the
 * AI rows whose soft-delete flags an adoption flips (A1–A3, A5), findings and
 * the conclusion (A6, A7), and context / flags / follow-up / tags (A8).
 */
import type {
  FindingStatement,
  FollowUp,
  Measurement,
  Report as MReport,
} from '@/models/report';

export interface ConclusionState {
  uuid?: string;
  value: string;
}

export interface WorksheetSnapshot {
  measurements: Measurement[];
  aiMeasurements: MReport['aiMeasurements'];
  context: MReport['context'];
  tags: MReport['tags'];
  findings: Record<string, FindingStatement[]>;
  conclusion: ConclusionState | null;
  selectedFlags: Set<string>;
  followUp: FollowUp | null;
}

export interface WorksheetHistoryApplier {
  getSnapshot: () => WorksheetSnapshot | null;
  applySnapshot: (snapshot: WorksheetSnapshot) => void;
}

let applier: WorksheetHistoryApplier | null = null;

export function registerWorksheetHistoryApplier(
  next: WorksheetHistoryApplier
): () => void {
  applier = next;
  return () => {
    if (applier === next) applier = null;
  };
}

export function getWorksheetSnapshot(): WorksheetSnapshot | null {
  return applier?.getSnapshot() ?? null;
}

export function applyWorksheetSnapshot(snapshot: WorksheetSnapshot): void {
  applier?.applySnapshot(snapshot);
}
